import { GridType } from 'common/types/scalarSpreadsheet';
import { Logger, parseDatabaseValue } from 'utilities';
import { toStoredAmount } from './cashFlowSign';
import { CashFlowColumnAlias, CashFlowColumnNames } from './constants';
import { CashFlowLedgerCellMap, CashFlowLedgerRowData } from './types';

const extractRowNumber = (key: string): number => {
  const match = key.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

export const CashFlowReverseParser = (params: { cells: CashFlowLedgerCellMap }) => {
  const { cells } = params;

  const cellToLedgerMapping = new Map<number, CashFlowLedgerRowData>();
  const investedAtLoadByRow = new Map<number, boolean | undefined>();

  Object.values(cells).forEach((cell): void => {
    const rowNumber = extractRowNumber(cell.key ?? '');
    if (!rowNumber) {
      Logger.warn(`Row number not found when parsing cell key: CellKey: ${cell.key}`);
      return;
    }

    // Skip row 1 - it's the header row
    if (rowNumber === 1) {
      return;
    }

    // Skip virtual rows
    if (cell.is_virtual) {
      return;
    }

    const isRelevantDataCell = CashFlowColumnNames.includes(cell.alias as CashFlowColumnAlias);
    if (!isRelevantDataCell) {
      return;
    }

    const parsedValue = parseDatabaseValue({
      value: cell.value,
      type: cell.dbType,
      gridType: cell.gridType as GridType,
      format: cell.format,
      allowEmptyValues: true,
    });

    const cellValue = parsedValue === '' ? null : parsedValue;

    const currentMapping =
      cellToLedgerMapping.get(rowNumber) ||
      ({
        id: cell.entityId,
        is_virtual: cell.is_virtual || false,
      } as CashFlowLedgerRowData);

    if (cell.alias === 'owner') {
      if (typeof cellValue === 'string') {
        if (cellValue.startsWith('fund-')) {
          currentMapping.owner_fund = parseInt(cellValue.replace('fund-', ''), 10);
          currentMapping.owner_company = null;
        } else if (cellValue.startsWith('company-')) {
          currentMapping.owner_company = parseInt(cellValue.replace('company-', ''), 10);
          currentMapping.owner_fund = null;
        }
      }
    } else if (cell.alias === 'entity') {
      if (typeof cellValue === 'string' && cellValue.startsWith('company-')) {
        currentMapping.entity_company = parseInt(cellValue.replace('company-', ''), 10);
      }
    } else if (cell.alias === 'all_security') {
      // Map all_security alias to security field
      // Handle both numeric values and string values that can be parsed to numbers
      if (typeof cellValue === 'number') {
        currentMapping.security = cellValue;
      } else if (typeof cellValue === 'string' && cellValue !== '') {
        const parsed = parseInt(cellValue, 10);
        currentMapping.security = Number.isNaN(parsed) ? null : parsed;
      } else {
        currentMapping.security = null;
      }
    } else {
      (currentMapping as any)[cell.alias as string] = cellValue;
    }

    // The Amount cell also carries the exclusion toggle and whether the row loaded as invested capital.
    if (cell.alias === CashFlowColumnAlias.AMOUNT) {
      investedAtLoadByRow.set(rowNumber, cell.investedAtLoad);
      if (cell.enabled !== undefined) {
        currentMapping.is_excluded_from_calculations = !cell.enabled;
      }
    }

    cellToLedgerMapping.set(rowNumber, currentMapping);
  });

  // Amount cells hold the displayed sign; convert back to the stored sign for the row's Type.
  return Array.from(cellToLedgerMapping.entries())
    .sort(([a], [b]) => a - b)
    .map(([rowNumber, item]) => ({
      ...item,
      cash_flow_type: item.cash_flow_type ?? null,
      amount: toStoredAmount(item.cash_flow_type, item.amount, investedAtLoadByRow.get(rowNumber)),
    }));
};
